import { useEffect, useState } from "react";
import { Navigate, NavLink, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BadgeCheck, CheckCircle2, CloudCheck, Home, RefreshCw, Smartphone } from "lucide-react";
import { useAppOutlet } from "../components/Shell";
import { primaryButtonClass, Status } from "../components/ui";
import { readCachedModule } from "../offline/assetCache";
import { loadModule, withPendingProgress } from "../offline/moduleDownload";
import { useWriteQueue } from "../offline/writeQueue";
import { errorMessage } from "../lib/api";
import { useLocalizedModule } from "../lib/localize";
import { dayForModule } from "../lib/pdDayRoutes";
import { usePDDays } from "../pddays/usePDDays";
import type { ModuleDetail } from "../types";

export function ModuleCompletion() {
  const { t } = useTranslation();
  const { moduleID = "" } = useParams();
  const { setHeaderTitle } = useAppOutlet();
  const queue = useWriteQueue();
  const [result, setResult] = useState<{
    id: string;
    module: ModuleDetail | null;
    error: string;
  } | null>(null);
  const module = useLocalizedModule(result?.id === moduleID ? result.module : null);
  const error = result?.id === moduleID ? result.error : "";
  const { all, dayNumbers } = usePDDays();
  const day = dayForModule(all, moduleID);
  const dayName = t("pdDayOrdinal", { number: (day && dayNumbers[day.id]) ?? "" });
  const title = t("pdDayCompleteTitle", { number: (day && dayNumbers[day.id]) ?? "" });

  useEffect(() => {
    setHeaderTitle(title);
    return () => setHeaderTitle(null);
  }, [title, setHeaderTitle]);

  useEffect(() => {
    let active = true;
    async function readCompletion() {
      const cached = await readCachedModule(moduleID);
      return cached ? withPendingProgress(cached) : loadModule(moduleID);
    }
    readCompletion()
      .then((value) => {
        if (active) setResult({ id: moduleID, module: value, error: "" });
      })
      .catch((cause) => {
        if (active) setResult({ id: moduleID, module: null, error: errorMessage(cause) });
      });
    return () => {
      active = false;
    };
  }, [moduleID]);

  if (error) return <Status variant="error" title={t("moduleUnavailable")} message={error} />;
  if (!module)
    return <Status variant="loading" title={t("loading")} message={t("openingModule")} />;
  if (!module.items.length) {
    return <Navigate to={`/modules/${encodeURIComponent(moduleID)}`} replace />;
  }

  const pending = queue.pending > 0;
  return (
    <div className="flex flex-col gap-5 pb-28 pt-4">
      <div
        className="mx-auto grid size-32 place-items-center rounded-full bg-success-bg"
        aria-hidden="true"
      >
        <div className="grid size-24 place-items-center rounded-full bg-success-text">
          <CheckCircle2 size={56} className="text-success-bg" />
        </div>
      </div>

      <section className="flex flex-col items-center gap-4 rounded-xl bg-surface p-5 text-center shadow-raised">
        <span className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-accent">
          <BadgeCheck size={14} />
          {t(pending ? "completionSaved" : "completionRecorded")}
        </span>
        <h1 className="text-2xl font-bold tracking-tight text-app-ink">{title}</h1>
        <p className="text-sm leading-relaxed text-app-copy">
          {t("moduleCompleteBody", { name: dayName })}
        </p>
      </section>

      <section className="flex flex-col gap-2 rounded-xl bg-accent-soft p-4">
        <div className="flex items-center justify-between gap-2 text-xs font-semibold text-app-ink">
          <span className="inline-flex items-center gap-1.5">
            {pending ? <Smartphone size={16} /> : <CloudCheck size={16} />}
            {t(pending ? "completionSavedDevice" : "completionSynced")}
          </span>
          {pending && (
            <span className="rounded-full bg-surface px-2 py-1 text-accent">
              {t("completionQueued")}
            </span>
          )}
        </div>
        <p className="text-xs leading-relaxed text-app-copy">
          {t(pending ? "completionSyncBody" : "completionSyncedBody")}
        </p>
        {pending && (
          <p className="inline-flex items-center gap-1 text-xs text-accent">
            <RefreshCw size={12} className={queue.syncing ? "animate-spin" : ""} />
            {queue.syncing ? t("offlineSyncing") : t("completionWaiting", { count: queue.pending })}
          </p>
        )}
        {queue.rejected > 0 && (
          <p className="text-xs text-warning-text">{t("completionSyncRejected")}</p>
        )}
      </section>

      <div className="fixed bottom-0 left-1/2 z-40 w-full max-w-md -translate-x-1/2 bg-app-canvas/90 p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] backdrop-blur-md">
        <NavLink
          to="/pd-days"
          className={`${primaryButtonClass} flex items-center justify-center gap-2 text-sm`}
          style={{ borderRadius: "8px" }}
        >
          <Home size={18} />
          {t("completionBackHome")}
        </NavLink>
      </div>
    </div>
  );
}
